/* @refresh reload */
import { render } from "solid-js/web";

import { App } from "@/app";
import { setServiceWorkerUpdate } from "@folio/shared";
import { isTauriRuntime } from "@/lib/platform";
import { hydrateSecureTokens, primeActiveNativeToken } from "@/lib/secure-token";
import { installNewTabAccountHandoff } from "@/lib/new-tab-handoff";
import "@/styles/global.css";

installNewTabAccountHandoff();

// PWA: auto-update with a SINGLE reload. The `sw.js` (workbox) already does `skipWaiting()` when it receives
// `{type:"SKIP_WAITING"}`; here, when a new SW is detected as INSTALLED after a controller already exists (=an
// update, not the first install), we request it → it takes control → `controllerchange`
// → we reload ONCE. Thus one user refresh is enough (before, the new SW stayed `waiting`).
// Dependency-free on purpose (avoids workbox-window). To make it FULLY automatic (reload
// inactive tabs without user action) see infra/PWA_UPDATES.md. In Tauri there is no SW → no-op.
/**
 * Telemetry beacon for PWA updates.
 *
 * The outcome has to survive a page reload, because the successful outcome IS a
 * reload: recording "applied" in module state and then calling `location.reload()`
 * would throw the value away a millisecond later, and the beacon would report
 * "applied" exactly never. So the hand-off goes through `sessionStorage` (per
 * tab, cleared on read). Nothing personal goes in it — one of four literal words.
 *
 * Wrapped because `sessionStorage` throws outright in a partitioned/blocked
 * third-party context, and a telemetry beacon may not take the app down.
 */
const SW_UPDATE_HANDOFF_KEY = "sw-update";

function rememberServiceWorkerUpdate(state: string): void {
  try {
    globalThis.sessionStorage?.setItem(SW_UPDATE_HANDOFF_KEY, state);
  } catch {
    // Storage denied — the beacon degrades to "none", the app is unaffected.
  }
}

if ("serviceWorker" in navigator) {
  // Read the hand-off left by the pre-reload page, if any.
  try {
    const carried = globalThis.sessionStorage?.getItem(SW_UPDATE_HANDOFF_KEY);
    if (carried === "applied") {
      setServiceWorkerUpdate("applied");
      globalThis.sessionStorage?.removeItem(SW_UPDATE_HANDOFF_KEY);
    }
  } catch {
    // Same rule: unreadable storage is not an error worth surfacing.
  }

  let reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading) {
      return;
    }
    reloading = true;
    rememberServiceWorkerUpdate("applied");
    window.location.reload();
  });
  window.addEventListener("load", () => {
    void navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => {
        // `register()` can resolve to `undefined` when the SW is blocked
        // (Playwright `serviceWorkers: "block"`, privacy modes, etc.): without this
        // guard, `reg.addEventListener` would throw an uncaught error on every
        // load. In those environments there is simply no auto-update to wire up.
        if (!reg) {
          return undefined;
        }
        reg.addEventListener("updatefound", () => {
          const next = reg.installing;
          if (!next) {
            return;
          }
          next.addEventListener("statechange", () => {
            // Only on UPDATE (a controller already exists); on the 1st install we do not reload.
            if (next.state === "installed" && navigator.serviceWorker.controller) {
              // Beacon: a new build is installed and waiting. A fleet where this
              // stays "pending" means updates install but never take over.
              setServiceWorkerUpdate("pending");
              // oxlint-disable-next-line unicorn/require-post-message-target-origin -- the target is a ServiceWorker, not a Window: postMessage does not accept targetOrigin here (false positive).
              next.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
        return undefined;
      })
      // A blocked/denied registration rejects the promise; swallowing it avoids an
      // unhandledrejection that pollutes the console (and breaks "no
      // serious errors" assertions). The PWA is progressive enhancement: if it does not register, the app
      // works the same.
      .catch(() => {
        // Beacon only: still swallowed, still no user-visible effect. A fleet
        // where this climbs means updates cannot reach anyone, which is
        // invisible from the server without this signal.
        setServiceWorkerUpdate("failed");
        return undefined;
      });
  });
}

const root = document.querySelector("#root");

if (!root) {
  throw new Error("Root element #root not found in index.html");
}
const rootEl: Element = root;

function mountApp(): void {
  render(() => <App />, rootEl);

  // Tell the index.html boot watchdog the app mounted: cancels the self-heal
  // reload and clears its one-shot guard (so a future stale-deploy can recover).
  (window as typeof window & { __appBootOk?: () => void }).__appBootOk?.();
}

if (isTauriRuntime()) {
  // Tauri DESKTOP: load the Bearer token(s) from the OS keychain into the
  // synchronous in-memory cache BEFORE the app mounts, so the very first
  // authenticated request resolves its token (keychain reads are async; the
  // hot read-path is sync). Best-effort — never block boot on a keychain error.
  try {
    await hydrateSecureTokens();
    // Phase 2: prime the RUST-held active token from the store so the
    // `api_request` proxy can inject the Bearer on the first authenticated
    // request. The token stays in Rust — JS never receives it.
    await primeActiveNativeToken();
  } catch (err) {
    console.warn("[secure-token] hydration failed, continuing:", err);
  }
}
// Web (cookie-BFF) renders immediately; Tauri renders once the cache is warm.
mountApp();

// Dismiss the first-paint loader baked into index.html once the app shell has
// mounted. We wait two animation frames so the freshly-rendered shell has
// actually painted before we fade the loader, avoiding a flash-to-blank.
function dismissAppLoader() {
  const loader = document.querySelector("#app-loader");
  if (!loader) {
    return;
  }
  loader.classList.add("app-loader--hidden");
  const LOADER_FADE_MS = 300;
  setTimeout(() => {
    loader.remove();
  }, LOADER_FADE_MS);
}
requestAnimationFrame(() => {
  requestAnimationFrame(dismissAppLoader);
});
